import type { Customer, EligibilityRequest } from "../types";

import { parsePhoneNumberAttributes } from "../modules/product-sale-flow/utils/parsePhoneNumberAttributes";
import { useSalesContext } from "../sales-context/SalesContext";
import { isInternationalCaller } from "../shared-utils/formatters/validatePhoneNumber";
import { isVmpSalesEnabled } from "../shared-utils/isVmpSalesEnabled";
import { useCallDetailsStore, useVerifiedCustomer } from "../stores";

const EXPERT_UNIVERSITY_SUFFIX = "test-training";

/**
 * The one place an eligibility request is built, so every check sends the same inputs. Each check mints
 * new USP quotes and invalidates the last ones, so a check that left out an input (the client, the VMP
 * flag, the subscriptions) would return quotes shaped differently from the one before it.
 *
 * `buildRequest` takes the customer to send. A caller that has just collected new details passes them
 * in; the render's customer is stale until the store write re-renders.
 */
export function useEligibilityRequest() {
    const { sessionId, partner, taskType, taskData, expertLocation, ivrInfo } = useSalesContext();
    const customer = useVerifiedCustomer(sessionId);
    const callDetails = useCallDetailsStore((state) => state.callDetails);

    // Left undefined when channel-share data hasn't supplied PROTECTED_SALES, so the API can tell
    // "not known" apart from "not protected" (it narrows VMP-flagged calls on an unknown value).
    const isCustomerInProtectedState =
        callDetails.PROTECTED_SALES === undefined ? undefined : callDetails.PROTECTED_SALES === "1";
    const vmpSalesEnabled = isVmpSalesEnabled(ivrInfo);
    const isExpertUniversitySimulatedVoiceTask = sessionId.includes(EXPERT_UNIVERSITY_SUFFIX);
    // Nothing writes customer.metadata.isInternationalCaller today — its only writer was the
    // McAfee-SaaS call-intro flow, now deleted. The ANI fallback is what supplies the attribute;
    // the metadata read stays so a future expert-entered value would still take precedence.
    const isInternational = customer?.metadata?.isInternationalCaller ?? isInternationalCaller(taskData.mdn);

    const buildRequest = (requestCustomer: Customer | undefined = customer): EligibilityRequest => ({
        asurionCallId: taskType.isVoice ? taskData.asurionCallId : undefined,
        partner,
        sessionId,
        customerInteractionId: taskData.customerInteractionId,
        c360Interaction: taskData.c360Interaction,
        reservationSid: taskData.reservationSid ?? sessionId,
        ...(isExpertUniversitySimulatedVoiceTask
            ? { customerMdn: taskData.mdn }
            : { ...parsePhoneNumberAttributes(requestCustomer?.mdn) }),
        address: requestCustomer?.address,
        customer: {
            firstName: requestCustomer?.firstName,
            lastName: requestCustomer?.lastName,
            email: requestCustomer?.email,
            accountNumber: requestCustomer?.accountNumber,
        },
        eligibilityAttributes: {
            currentPlan: requestCustomer?.productName,
            isInternationalCaller: isInternational,
            chatSourceApp: taskType.isChat ? taskData.chatSourceApp : undefined,
            currentSubscriptions: requestCustomer?.currentSubscriptions,
            expertLocation,
            isCustomerInProtectedState,
            client: callDetails.CLIENT,
            isVmpSalesEnabled: vmpSalesEnabled,
        },
        salesChannel: taskData.salesChannel as EligibilityRequest["salesChannel"],
    });

    return {
        buildRequest,
        /** The request's inputs that come from the call rather than the customer; a late change to one needs a re-check. */
        callInputs: {
            client: callDetails.CLIENT,
            isCustomerInProtectedState,
            isVmpSalesEnabled: vmpSalesEnabled,
            isInternationalCaller: isInternational,
        },
    };
}
